/**
 * Minimal i18n scaffold for tenant UI catalogs.
 *
 * - Baseline: pt-BR (complete for every adopted key)
 * - Overlay: pt-PT (lexical differences only)
 * - Resolution: localeProfile.locale — never infer from country
 * - Fallback: overlay → pt-BR → key
 *
 * See docs/ADR_I18N_LOCALE_CATALOGS.md
 */

import { getTenantLocale } from "../utils/locale-profile.js";
import { catalogPtBR } from "./catalogs/pt-BR.js";
import { catalogPtPT } from "./catalogs/pt-PT.js";

const BASE_LOCALE = "pt-BR";
const VALID_UI_LOCALES = new Set(["pt-BR", "pt-PT"]);

const CATALOGS = Object.freeze({
  "pt-BR": catalogPtBR,
  "pt-PT": catalogPtPT,
});

/** Flatpickr ships a single Portuguese pack (`pt`) for both variants today. */
const FLATPICKR_LOCALE_BY_UI = Object.freeze({
  "pt-BR": "pt",
  "pt-PT": "pt",
});

const TOKEN_RE = /\{(\w+)\}/g;

/**
 * Normalize a UI locale token. Invalid / empty → pt-BR.
 * @param {unknown} raw
 * @returns {"pt-BR"|"pt-PT"}
 */
export function normalizeUiLocale(raw) {
  const value = String(raw || "")
    .trim()
    .replace(/_/g, "-");
  const lowered = value.toLowerCase();
  if (lowered === "pt-br") return "pt-BR";
  if (lowered === "pt-pt") return "pt-PT";
  if (VALID_UI_LOCALES.has(value)) return /** @type {"pt-BR"|"pt-PT"} */ (value);
  return BASE_LOCALE;
}

/**
 * Resolve active UI locale from explicit arg or tenant profile.
 * @param {unknown} [locale]
 * @returns {"pt-BR"|"pt-PT"}
 */
export function resolveUiLocale(locale) {
  if (locale === undefined || locale === null || locale === "") {
    try {
      return normalizeUiLocale(getTenantLocale());
    } catch {
      return BASE_LOCALE;
    }
  }
  return normalizeUiLocale(locale);
}

function isDevDiagnostics() {
  try {
    if (typeof window !== "undefined" && window.CONFIG?.STAGE) {
      return window.CONFIG.STAGE !== "prod";
    }
  } catch {
    /* ignore */
  }
  return false;
}

function warnMissingKey(key, locale) {
  if (!isDevDiagnostics()) return;
  try {
    console.warn(`[i18n] Missing key "${key}" for locale "${locale}"`);
  } catch {
    /* ignore */
  }
}

/**
 * Lookup raw catalog string (no interpolation / plural).
 * @param {string} key
 * @param {"pt-BR"|"pt-PT"} locale
 * @returns {string|null}
 */
function lookupRaw(key, locale) {
  if (locale !== BASE_LOCALE) {
    const overlay = CATALOGS[locale];
    if (overlay && Object.prototype.hasOwnProperty.call(overlay, key)) {
      const value = overlay[key];
      if (typeof value === "string" && value.length > 0) return value;
    }
  }
  const base = CATALOGS[BASE_LOCALE];
  if (base && Object.prototype.hasOwnProperty.call(base, key)) {
    const value = base[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return null;
}

/**
 * Safe token interpolation. Only own enumerable params; values coerced to string.
 * Missing params leave `{name}` intact. Never interprets as HTML.
 * @param {string} template
 * @param {Record<string, unknown>} [params]
 * @returns {string}
 */
export function interpolate(template, params) {
  if (!params || typeof params !== "object") return template;
  return String(template).replace(TOKEN_RE, (match, name) => {
    if (!Object.prototype.hasOwnProperty.call(params, name)) return match;
    const value = params[name];
    if (value === undefined || value === null) return match;
    return String(value);
  });
}

/**
 * Resolve plural category via Intl.PluralRules (one / other for pt*).
 * @param {string} keyBase - e.g. "lead.rows"
 * @param {number} count
 * @param {"pt-BR"|"pt-PT"} locale
 * @returns {string} full key e.g. "lead.rows.one"
 */
function resolvePluralKey(keyBase, count, locale) {
  let category = "other";
  try {
    const rules = new Intl.PluralRules(locale);
    const resolved = rules.select(Number(count));
    category = resolved === "one" ? "one" : "other";
  } catch {
    category = Number(count) === 1 ? "one" : "other";
  }
  return `${keyBase}.${category}`;
}

/**
 * Translate a catalog key.
 *
 * Options:
 * - `locale` — override tenant locale
 * - `count` — selects `{key}.one` / `{key}.other` when key has no direct entry
 * - params — any other own props are interpolation tokens (`{name}`)
 *
 * @param {string} key
 * @param {Record<string, unknown>} [options]
 * @returns {string}
 */
export function t(key, options = {}) {
  const safeKey = String(key || "");
  if (!safeKey) return "";

  const locale = resolveUiLocale(options?.locale);
  const { locale: _ignoredLocale, count, ...params } = options || {};

  let lookupKey = safeKey;
  if (count !== undefined && count !== null) {
    const direct = lookupRaw(safeKey, locale);
    if (direct === null) {
      lookupKey = resolvePluralKey(safeKey, Number(count), locale);
    }
  }

  const raw = lookupRaw(lookupKey, locale);
  if (raw === null) {
    warnMissingKey(lookupKey, locale);
    return lookupKey;
  }

  const withCount =
    count !== undefined && count !== null && !Object.prototype.hasOwnProperty.call(params, "count")
      ? { ...params, count }
      : params;

  return interpolate(raw, withCount);
}

/**
 * Map UI locale → Flatpickr locale id.
 * @param {unknown} [localeOrProfile]
 * @returns {string}
 */
export function getFlatpickrLocale(localeOrProfile) {
  let ui = BASE_LOCALE;
  if (localeOrProfile && typeof localeOrProfile === "object" && "locale" in localeOrProfile) {
    ui = resolveUiLocale(localeOrProfile.locale);
  } else {
    ui = resolveUiLocale(localeOrProfile);
  }
  return FLATPICKR_LOCALE_BY_UI[ui] || "pt";
}

/**
 * Set document.documentElement.lang from tenant / explicit locale.
 * Idempotent; no-ops without document.
 * @param {unknown} [localeOrProfile]
 * @returns {"pt-BR"|"pt-PT"}
 */
export function applyDocumentLocale(localeOrProfile) {
  let ui = BASE_LOCALE;
  if (localeOrProfile && typeof localeOrProfile === "object" && "locale" in localeOrProfile) {
    ui = resolveUiLocale(localeOrProfile.locale);
  } else if (localeOrProfile !== undefined && localeOrProfile !== null) {
    ui = resolveUiLocale(localeOrProfile);
  } else {
    ui = resolveUiLocale();
  }

  if (typeof document !== "undefined" && document.documentElement) {
    document.documentElement.lang = ui;
  }
  return ui;
}

const ATTR_MAP = Object.freeze({
  "data-i18n": "text",
  "data-i18n-placeholder": "placeholder",
  "data-i18n-title": "title",
  "data-i18n-aria-label": "aria-label",
});

/**
 * Apply declarative i18n attributes under root (default: document).
 * Uses textContent / attribute setters — never innerHTML.
 * @param {ParentNode} [root]
 * @param {unknown} [locale]
 * @returns {number} nodes updated
 */
export function applyDomI18n(root, locale) {
  if (typeof document === "undefined") return 0;
  const scope = root || document;
  const resolved = resolveUiLocale(locale);
  let updated = 0;

  for (const [attr, target] of Object.entries(ATTR_MAP)) {
    const nodes = scope.querySelectorAll?.(`[${attr}]`);
    if (!nodes) continue;
    nodes.forEach((el) => {
      const key = el.getAttribute(attr);
      if (!key) return;
      const value = t(key, { locale: resolved });
      if (target === "text") {
        el.textContent = value;
      } else {
        el.setAttribute(target, value);
      }
      updated += 1;
    });
  }

  return updated;
}

/**
 * Apply document lang + declarative DOM translations after tenant profile is ready.
 * @param {unknown} [localeOrProfile]
 * @param {ParentNode} [root]
 * @returns {"pt-BR"|"pt-PT"}
 */
export function hydrateUiLocale(localeOrProfile, root) {
  const ui = applyDocumentLocale(localeOrProfile);
  applyDomI18n(root, ui);
  return ui;
}

/** @returns {Readonly<Record<string, string>>} */
export function getBaseCatalog() {
  return CATALOGS[BASE_LOCALE];
}

/** @returns {Readonly<Record<string, string>>} */
export function getOverlayCatalog(locale = "pt-PT") {
  const key = normalizeUiLocale(locale);
  return CATALOGS[key] || CATALOGS[BASE_LOCALE];
}

export { BASE_LOCALE, CATALOGS };
